/* Stato della barra dal pulsante/form Focal corrente; nessuna Cart API duplicata. */
(() => {
  if (!customElements.get('extro-sticky-atc')) {
    class StickyAtc extends HTMLElement {
      connectedCallback() {
        this.scope = this.closest('[data-extro-product-hover]');
        if (!this.scope) return;
        this.button = this.querySelector('button');
        this.label = this.querySelector('.sticky-atc-button-content');
        this.onUpdate = () => {
          if (this.updateQueued) return;
          this.updateQueued = true;
          queueMicrotask(() => { this.updateQueued = false; if (this.isConnected) this.refresh(); });
        };
        this.onClick = () => {
          this.refresh();
          if (this.button.disabled) return;
          if (this.canAdd) {
            this.button.disabled = true;
            this.main.click(); // Quantita', proprieta', validazione e cassetto originali.
            this.onUpdate();
          }
        };
        this.intersection = new IntersectionObserver(entries => {
          for (const entry of entries) if (entry.target === this.main) this.refresh();
        });
        this.buttonObserver = new MutationObserver(this.onUpdate);
        this.sizeObserver = new ResizeObserver(() => {
          if (this.classList.contains('is-visible')) document.documentElement.style.setProperty('--cart-notification-offset', `${this.clientHeight}px`);
        });
        this.sizeObserver.observe(this);
        this.scope.addEventListener('variant:change', this.onUpdate);
        this.scope.addEventListener('product:rerender', this.onUpdate, true);
        this.scope.addEventListener('extro:variant-pending', this.onUpdate);
        this.button.addEventListener('click', this.onClick);
        this.frame = requestAnimationFrame(() => this.refresh());
      }
      disconnectedCallback() {
        cancelAnimationFrame(this.frame);
        this.intersection?.disconnect();
        this.buttonObserver?.disconnect();
        this.sizeObserver?.disconnect();
        document.documentElement.style.removeProperty('--cart-notification-offset');
        this.scope?.removeEventListener('variant:change', this.onUpdate);
        this.scope?.removeEventListener('product:rerender', this.onUpdate, true);
        this.scope?.removeEventListener('extro:variant-pending', this.onUpdate);
        this.button?.removeEventListener('click', this.onClick);
      }
      setVisible(visible) {
        this.classList.toggle('is-visible', visible);
        this.setAttribute('aria-hidden', String(!visible));
        this.inert = !visible;
        if (visible) document.documentElement.style.setProperty('--cart-notification-offset', `${this.clientHeight}px`);
        else document.documentElement.style.removeProperty('--cart-notification-offset');
      }
      refresh() {
        const main = this.scope.querySelector('[data-product-add-to-cart-button]');
        if (main !== this.main) {
          this.intersection.disconnect();
          this.buttonObserver.disconnect();
          this.main = main;
          if (main) {
            this.intersection.observe(main);
            this.buttonObserver.observe(main, { attributes: true, attributeFilter: ['disabled', 'aria-busy', 'data-variant-id', 'data-variant-available'] });
          }
        }
        if (!main) { this.setVisible(false); return; }
        const busy = main.getAttribute('aria-busy') === 'true' || this.scope.hasAttribute('data-extro-variant-pending');
        const selectedId = main.form?.elements.namedItem('id')?.value;
        this.canAdd = main.dataset.variantAvailable === 'true' && !!selectedId && selectedId === main.dataset.variantId && !main.disabled;
        this.button.classList.toggle('can-add-to-cart', this.canAdd);
        const text = 'AGGIUNGI AL CARRELLO >';
        if (this.label.textContent !== text) this.label.textContent = text;
        this.button.disabled = busy || !this.canAdd;
        this.button.classList.toggle('is-loading', busy);
        this.button.setAttribute('aria-busy', String(busy));
        const rect = main.getBoundingClientRect();
        this.setVisible(this.canAdd && !busy && (rect.bottom <= 0 || rect.top >= innerHeight));
      }
    }
    customElements.define('extro-sticky-atc', StickyAtc);
  }

  if (!customElements.get('extro-paypal-message')) {
    // Il SDK serve soltanto quando il prezzo e il messaggio si avvicinano alla vista.
    // Una sola richiesta per pagina, mantenendo il rendering e gli importi del SDK.
    const sdkRequests = new Map();
    function loadPaypal(template) {
      const original = template?.content.querySelector('script[src]');
      if (!original) return Promise.resolve();
      const src = original.src;
      if (!sdkRequests.has(src)) {
        const request = new Promise((resolve, reject) => {
          const script = document.createElement('script');
          for (const attribute of original.attributes) script.setAttribute(attribute.name, attribute.value);
          script.async = true;
          script.addEventListener('load', resolve, { once: true });
          script.addEventListener('error', () => { script.remove(); reject(new Error('PayPal non disponibile')); }, { once: true });
          document.head.append(script);
        });
        sdkRequests.set(src, request);
        request.catch(() => sdkRequests.delete(src));
      }
      return sdkRequests.get(src);
    }
    class PaypalMessage extends HTMLElement {
      connectedCallback() {
        const template = this.querySelector('template[data-extro-paypal-sdk]');
        if (template) {
          const load = () => loadPaypal(template).then(() => this.sdkObserver?.disconnect()).catch(() => {});
          if ('IntersectionObserver' in window) {
            this.sdkObserver = new IntersectionObserver(entries => {
              if (entries.some(entry => entry.isIntersecting)) load();
            }, { rootMargin: '0px' });
            this.sdkObserver.observe(this);
          } else load();
        }
        this.scope = this.closest('.product-form');
        this.onVariant = event => {
          const message = this.querySelector('[data-pp-message]');
          if (!message) return;
          const variant = event.detail.variant;
          this.style.visibility = variant ? '' : 'hidden';
          if (!variant || Number(message.dataset.ppAmount) === variant.price / 100) return;
          // Il SDK osserva data-pp-amount: non ricreare iframe o richiamare render().
          const height = message.getBoundingClientRect().height;
          if (height > 0) message.style.minHeight = `${height}px`;
          message.dataset.ppAmount = (variant.price / 100).toFixed(2);
        };
        this.scope?.addEventListener('variant:change', this.onVariant);
        this.resize = new ResizeObserver(entries => {
          const width = entries[0].contentRect.width;
          if (this.width != null && Math.abs(width - this.width) > 1) this.querySelector('[data-pp-message]')?.style.removeProperty('min-height');
          this.width = width;
        });
        this.resize.observe(this);
      }
      disconnectedCallback() {
        this.scope?.removeEventListener('variant:change', this.onVariant);
        this.resize?.disconnect();
        this.sdkObserver?.disconnect();
      }
    }
    customElements.define('extro-paypal-message', PaypalMessage);
  }
})();
